import React, { useEffect, useRef, useState } from 'react'
import LogoIcon from '../LogoIcon'
import AvatarWithDropdown from '../AvatarWithDropdown'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import { trackEvent } from '@/lib/trackEvent'
import useSaveGameData from '@/hooks/useSaveGameData'
import { RefreshCcw } from 'lucide-react'
import saved from '@/assets/cloud-check.svg'
import { discordLink } from '@/constants'
import { displayTutorial } from '@/store/features/tutorialSlice'
import { axios } from '@/services/api/axiosInstance'
import { endpoints } from '@/services/api/endpoints'
import { updateTitle } from '@/store/features/gameConfigSlice'
import { redirectToPlayApp } from '@/utils/redirect'
import { toast } from 'sonner'
import { GrHelpBook } from 'react-icons/gr'
import DiscordButton from '../DiscordAnimatedButton'
import cookies from '@/utils/cookies'
import PublishButton from './Publish'
import CreatorStepper from '@/components/shared/CreatorStepper'
import { setCreationStep } from '@/store/features/stepperSlice'

interface NavbarProps {
  isGameEditor?: boolean
  onPublish?: () => void
}

const Navbar: React.FC<NavbarProps> = ({ isGameEditor = false, onPublish }) => {
  const { loading } = useSaveGameData()
  const userInfo = useSelector((state: RootState) => state.user)
  const gameStatus = useSelector(
    (state: RootState) => state.gameDetails.gameStatus
  )
  const reduxGameTitle = useSelector(
    (state: RootState) => state.gameConfig.title
  )
  const gameId = useSelector((state: RootState) => state.gameDetails.gameId)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editedTitle, setEditedTitle] = useState(reduxGameTitle ?? '')
  const [showSavedText, setShowSavedText] = useState(false)
  const accessToken = cookies.get('ai_tk')

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const prevLoadingRef = useRef(loading)

  useEffect(() => {
    let timer: NodeJS.Timeout

    if (prevLoadingRef.current && !loading) {
      setShowSavedText(true)
      timer = setTimeout(() => {
        setShowSavedText(false)
      }, 3000)
    }

    prevLoadingRef.current = loading

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [loading])

  useEffect(() => {
    setEditedTitle(reduxGameTitle ?? '')
  }, [reduxGameTitle])

  const login = () => {
    trackEvent('Login Clicked', { location: 'Navbar' })
    redirectToPlayApp()
  }

  const handleDiscordClick = () => {
    trackEvent('Discord Link Clicked', {
      location: 'Navbar',
      type: 'Button',
    })
    window.open(discordLink, '_blank')
  }

  const handlePublish = () => {
    trackEvent('Publish Game Clicked', { location: 'Navbar', gameStatus })
    dispatch(setCreationStep(3))
    if (onPublish) onPublish()
  }

  const handleEditTitle = () => {
    setIsEditingTitle(true)
    trackEvent('Started Editing Game Title', {
      game_id: gameId,
      current_title: reduxGameTitle,
    })
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTitle(e.target.value)
  }
  const handleSaveTitle = async (
    e: React.FormEvent | React.MouseEvent<HTMLFormElement>
  ) => {
    e.preventDefault()
    if (editedTitle.trim() === '') {
      return toast('Game Title Cannot Be Empty')
    }

    const oldTitle = reduxGameTitle
    dispatch(updateTitle(editedTitle))
    setIsEditingTitle(false)

    if (editedTitle !== oldTitle) {
      trackEvent('Changed Game Title', {
        game_id: gameId,
        old_title: oldTitle,
        new_title: editedTitle,
      })
    }

    try {
      await axios.patch(
        endpoints.gameUpdate.url,
        { game_title: editedTitle },
        { params: { game_id: gameId } }
      )
      trackEvent('Game Title Updated', { newTitle: editedTitle })
    } catch (e) {
      console.log(e)
    }
  }

  const renderSaveStatus = () => {
    return loading ? (
      <div className="flex items-center text-[#7B7B7B]">
        <RefreshCcw className="mr-2 size-4 animate-spin" />
        <span className="mr-1 text-sm">Saving</span>
        <div className="flex">
          <span className="dot-1">.</span>
          <span className="dot-2">.</span>
          <span className="dot-3">.</span>
        </div>
      </div>
    ) : (
      <div className="flex items-center text-white">
        <img src={saved} alt="saved icon" className="size-6" />
        {showSavedText && (
          <span className="ml-2 text-sm text-[#7B7B7B]">Saved</span>
        )}
      </div>
    )
  }

  const renderGameTitle = () => {
    if (!isGameEditor) {
      return null
    }
    return (
      <form
        onSubmit={handleSaveTitle}
        className="hidden flex-1 items-center gap-2 overflow-hidden truncate lg:flex"
      >
        {isEditingTitle ? (
          <input
            type="text"
            value={editedTitle}
            onChange={handleTitleChange}
            className="w-full border-b border-white bg-transparent text-white focus:outline-none"
            onBlur={handleSaveTitle}
            autoFocus
          />
        ) : (
          <h1
            className="flex items-center text-[14px] font-semibold"
            onClick={handleEditTitle}
          >
            <span className="text-white">Editing:</span>
            <span className="ml-2 text-[#7B7B7B]">{reduxGameTitle}</span>
          </h1>
        )}
      </form>
    )
  }

  return (
    <div className="relative z-10 flex h-14 w-full items-center gap-4 px-2 text-sm lg:h-16 lg:px-8 lg:text-base">
      <div className="flex-1 items-center gap-2 lg:flex lg:gap-4 [&>a.active]:text-white [&>a]:hidden [&>a]:items-center [&>a]:gap-2 [&>a]:text-white/50 lg:[&>a]:flex">
        <div className="relative h-12">
          <LogoIcon />
        </div>

        {reduxGameTitle && renderGameTitle()}
      </div>

      {/* ✅ FIX: Removed the 'isGameEditor' check */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <CreatorStepper />
      </div>

      <div className="flex items-center gap-2 text-white lg:gap-4">
        {/* Undo/Redo buttons moved to Sidebar.tsx */}
      </div>

      <div className="flex flex-1 items-center justify-end gap-2 lg:gap-4 ">
        {isGameEditor && (
          <>
            {renderSaveStatus()}
            <div className="h-6 border-r border-gray-700" />
          </>
        )}

        <button
          className="flex size-[42px] items-center justify-center rounded-full bg-white/10"
          onClick={() => {
            dispatch(displayTutorial())
            trackEvent('Tutorial Icon Clicked', {
              location: 'navbar',
            })
          }}
        >
          <GrHelpBook className="animate-mini-scale size-5 cursor-pointer bg-white/10 text-white" />
        </button>
        <button
          onClick={handleDiscordClick}
          className="hidden h-[42px] lg:inline-flex"
        >
          <DiscordButton />
        </button>
        {isGameEditor && (
          <PublishButton
            handlePublish={handlePublish}
            gameStatus={gameStatus}
            publishStatus={gameStatus === 'published'}
          />
        )}
        {userInfo?.userId && accessToken ? (
          <AvatarWithDropdown userInfo={userInfo} />
        ) : (
          <button
            className="primary-button hidden h-[42px] lg:inline-flex"
            onClick={login}
          >
            Login
          </button>
        )}
      </div>
    </div>
  )
}

export default Navbar
